import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight } from "lucide-react";
import { SERVICES_DATA } from "../lib/servicesData";
import { useSEO } from "../lib/useSEO";
import { organisationSchema, localBusinessSchema, SITE_URL } from "../lib/seo";

export default function ServiceDetailPage() {
    const { slug } = useParams();
    const service = SERVICES_DATA[slug];

    useSEO({
        title: service?.metaTitle || "Service | Optimum Hospitality Solutions",
        description: service?.metaDescription || "",
        path: `/services/${slug}`,
        keywords: service?.keywords,
    });

    if (!service) return <Navigate to="/services" replace />;

    // FAQ schema
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    // Service schema (provider references @id of the organisation graph)
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.title,
        provider: { "@id": `${SITE_URL}/#organization` },
        areaServed: { "@type": "Country", name: "United Kingdom" },
        description: service.metaDescription,
        url: `${SITE_URL}/services/${slug}`,
    };

    // Breadcrumb schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
            { "@type": "ListItem", position: 3, name: service.title, item: `${SITE_URL}/services/${slug}` },
        ],
    };

    return (
        <div data-testid={`service-page-${slug}`}>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />

            {/* Breadcrumb */}
            <nav data-testid="service-breadcrumb" className="border-b border-brand-border bg-brand-stone/60">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-4 flex items-center gap-2 text-xs text-brand-ink/60">
                    <Link to="/" className="hover:text-brand-navy">Home</Link>
                    <ChevronRight size={12} className="text-brand-ink/40" />
                    <Link to="/services" className="hover:text-brand-navy">Services</Link>
                    <ChevronRight size={12} className="text-brand-ink/40" />
                    <span className="text-brand-navy font-medium truncate">{service.title}</span>
                </div>
            </nav>

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">{service.overline}</span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">{service.h1}</h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-3xl">{service.intro}</p>
                    <div className="mt-10">
                        <Link
                            to="/booking?type=discovery"
                            data-testid="service-hero-cta"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                        >
                            Book a complimentary discovery call <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Key challenges addressed</span>
                        <h2 className="font-display text-3xl mt-5 text-brand-navy">Where this service applies.</h2>
                    </div>
                    <ul className="lg:col-span-7 space-y-4">
                        {service.challenges.map((c, i) => (
                            <li key={c} className="flex gap-4 pb-4 border-b border-brand-border last:border-0 text-brand-ink/80 leading-relaxed">
                                <span className="text-brand-champagne shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Benefits</span>
                        <h2 className="font-display text-3xl mt-5 text-brand-navy">What changes for the business.</h2>
                    </div>
                    <ul className="lg:col-span-7 space-y-4">
                        {service.benefits.map((b) => (
                            <li key={b} className="flex gap-4 text-brand-ink/80 leading-relaxed">
                                <Check className="text-brand-champagne shrink-0 mt-1" size={18} strokeWidth={1.5} />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Our approach</span>
                    <h2 className="font-display text-3xl sm:text-4xl mt-5 text-brand-navy">How we deliver this engagement.</h2>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border">
                        {service.approach.map((p) => (
                            <div key={p.n} className="bg-brand-stone p-8">
                                <div className="text-brand-champagne font-display text-2xl">{p.n}</div>
                                <h3 className="font-display text-xl mt-3 text-brand-navy">{p.t}</h3>
                                <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">{p.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Why Optimum</span>
                        <h2 className="font-display text-3xl mt-5 text-brand-navy">Why clients choose us for this work.</h2>
                    </div>
                    <ul className="lg:col-span-7 space-y-4">
                        {service.why.map((w) => (
                            <li key={w} className="flex gap-4 text-brand-ink/80 leading-relaxed pb-4 border-b border-brand-border last:border-0">
                                <Check className="text-brand-champagne shrink-0 mt-1" size={18} strokeWidth={1.5} />
                                <span>{w}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">FAQ</span>
                    <h2 className="font-display text-3xl sm:text-4xl mt-5 text-brand-navy">Frequently asked questions.</h2>
                    <div className="mt-12 space-y-6 max-w-4xl">
                        {service.faqs.map((f) => (
                            <div key={f.q} className="border-b border-brand-border pb-6">
                                <h3 className="font-display text-lg text-brand-navy">{f.q}</h3>
                                <p className="mt-3 text-brand-ink/75 leading-relaxed">{f.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Related services</span>
                    <h2 className="font-display text-3xl mt-5 text-brand-navy">Often paired with.</h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {service.relatedSlugs.map((rs) => {
                            const r = SERVICES_DATA[rs];
                            if (!r) return null;
                            return (
                                <Link
                                    key={rs}
                                    to={`/services/${rs}`}
                                    data-testid={`related-service-${rs}`}
                                    className="border border-brand-border bg-brand-stone p-6 hover:border-brand-champagne transition-colors"
                                >
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">Service</div>
                                    <h3 className="font-display text-lg mt-3 text-brand-navy">{r.title}</h3>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-navy">Read more <ArrowUpRight size={14} /></span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-brand-navy" data-testid="service-bottom-cta">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-stone">
                            Ready to improve operational performance, profitability and guest experience?
                        </h2>
                        <p className="mt-5 text-brand-stone/75 max-w-2xl text-lg">
                            Schedule a confidential consultation with Optimum Hospitality Solutions.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right flex flex-wrap lg:justify-end gap-3">
                        <Link
                            to="/booking"
                            data-testid="service-book-consultation"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white transition-colors"
                        >
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-7 py-3.5 border border-brand-champagne text-brand-stone text-sm tracking-wide hover:bg-brand-champagne hover:text-brand-navy"
                        >
                            Contact us
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
